/**
 * Predictive Search – AI-enhanced autocomplete
 * Uses Shopify Predictive Search API with semantic matching (Winter '26)
 * No jQuery • Debounced • AbortController • Keyboard nav • ARIA accessible
 * ~8KB unminified
 */
(function () {
  'use strict';

  /* ---- Constants ---- */
  const DEBOUNCE_MS = 300;
  const LIMITS = { products: 4, collections: 3, articles: 3 };
  const RESOURCE_TYPES = 'product,collection,article';
  const MIN_QUERY_LENGTH = 2;
  const CACHE_MAX = 50;

  /* ---- Utility helpers ---- */
  function debounce(fn, wait) {
    let timer;
    return function () {
      const args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function highlightMatch(text, query) {
    if (!query) return escapeHtml(text);
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp('(' + escaped + ')', 'gi');
    return escapeHtml(text).replace(re, '<mark class="predictive-search__highlight">$1</mark>');
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number' && typeof cents !== 'string') return '';
    var amount = (typeof cents === 'string' ? parseInt(cents, 10) : cents) / 100;
    var format = window.moneyFormat || '${{amount}}';
    return format
      .replace('{{amount}}', amount.toFixed(2))
      .replace('{{amount_no_decimals}}', Math.round(amount).toString())
      .replace('{{amount_with_comma_separator}}', amount.toFixed(2).replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(amount).toString());
  }

  /* ---- Simple LRU cache ---- */
  var searchCache = Object.create(null);
  var cacheKeys = [];

  function getCached(key) {
    return searchCache[key] || null;
  }

  function setCache(key, data) {
    if (cacheKeys.length >= CACHE_MAX) {
      var oldest = cacheKeys.shift();
      delete searchCache[oldest];
    }
    searchCache[key] = data;
    cacheKeys.push(key);
  }

  /* ---- Build result item HTML ---- */
  function buildProductItem(product, query) {
    var image = product.image
      ? '<img class="predictive-search__product-image" src="' + product.image.replace(/(\.[a-z]+)(\?|$)/, '_180x$1$2') + '" alt="' + escapeHtml(product.title) + '" width="60" height="60" loading="lazy">'
      : '<div class="predictive-search__product-image predictive-search__product-image--placeholder"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg></div>';

    var priceHtml = '';
    var variant = product.variants && product.variants[0];
    if (variant) {
      var currentPrice = formatMoney(variant.price);
      if (variant.compare_at_price && parseInt(variant.compare_at_price, 10) > parseInt(variant.price, 10)) {
        priceHtml = '<span class="predictive-search__price predictive-search__price--sale">' + currentPrice + '</span>' +
          '<span class="predictive-search__price predictive-search__price--compare"><s>' + formatMoney(variant.compare_at_price) + '</s></span>';
      } else {
        priceHtml = '<span class="predictive-search__price">' + currentPrice + '</span>';
      }
    }

    var available = product.available !== false;
    var badge = available ? '' : '<span class="predictive-search__badge predictive-search__badge--sold-out">Sold Out</span>';

    return '<li class="predictive-search__item predictive-search__item--product" role="option" tabindex="-1">' +
      '<a href="' + product.url + '" class="predictive-search__link predictive-search__link--product" data-predictive-search-close>' +
        image +
        '<div class="predictive-search__product-info">' +
          '<span class="predictive-search__product-title">' + highlightMatch(product.title, query) + '</span>' +
          (product.vendor ? '<span class="predictive-search__product-vendor">' + escapeHtml(product.vendor) + '</span>' : '') +
          '<div class="predictive-search__product-meta">' + priceHtml + badge + '</div>' +
        '</div>' +
      '</a>' +
    '</li>';
  }

  function buildCollectionItem(collection, query) {
    return '<li class="predictive-search__item predictive-search__item--collection" role="option" tabindex="-1">' +
      '<a href="' + collection.url + '" class="predictive-search__link predictive-search__link--collection" data-predictive-search-close>' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/></svg>' +
        '<span class="predictive-search__collection-title">' + highlightMatch(collection.title, query) + '</span>' +
      '</a>' +
    '</li>';
  }

  function buildArticleItem(article, query) {
    var dateStr = '';
    if (article.published_at) {
      try {
        dateStr = new Date(article.published_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (e) { /* noop */ }
    }
    var blogName = article.blog && article.blog.title ? article.blog.title : '';
    return '<li class="predictive-search__item predictive-search__item--article" role="option" tabindex="-1">' +
      '<a href="' + article.url + '" class="predictive-search__link predictive-search__link--article" data-predictive-search-close>' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8V6Z"/></svg>' +
        '<div class="predictive-search__article-info">' +
          '<span class="predictive-search__article-title">' + highlightMatch(article.title, query) + '</span>' +
          '<span class="predictive-search__article-meta">' +
            (blogName ? escapeHtml(blogName) : '') +
            (blogName && dateStr ? ' · ' : '') +
            (dateStr ? dateStr : '') +
          '</span>' +
        '</div>' +
      '</a>' +
    '</li>';
  }

  /* ---- Main controller ---- */
  function PredictiveSearchController(container) {
    this.container = container;
    var baseSuggestUrl = container.getAttribute('data-routes-suggest') || '/search/suggest';
    /* Shopify requires .json extension for JSON responses (without section_id) */
    this.suggestUrl = baseSuggestUrl.replace(/\.json$/, '') + '.json';
    this.searchUrl = container.getAttribute('data-routes-search') || '/search';

    /* DOM refs */
    this.resultsWrapper = container.querySelector('[data-predictive-search-results]');
    this.loadingEl = container.querySelector('[data-predictive-search-loading]');
    this.noResultsEl = container.querySelector('[data-predictive-search-no-results]');
    this.footerEl = container.querySelector('[data-predictive-search-footer]');
    this.viewAllBtn = container.querySelector('[data-predictive-search-view-all]');
    this.statusEl = container.querySelector('[data-predictive-search-status]');

    this.groups = {
      products: {
        wrapper: container.querySelector('[data-group="products"]'),
        list: container.querySelector('[data-group-list="products"]')
      },
      collections: {
        wrapper: container.querySelector('[data-group="collections"]'),
        list: container.querySelector('[data-group-list="collections"]')
      },
      articles: {
        wrapper: container.querySelector('[data-group="articles"]'),
        list: container.querySelector('[data-group-list="articles"]')
      }
    };

    /* State */
    this.abortController = null;
    this.activeQuery = '';
    this.selectedIndex = -1;
    this.isOpen = false;

    /* Find the closest search input(s) — attach to parent form's input */
    this.inputs = [];
    this._findInputs();
    this._bindEvents();
  }

  PredictiveSearchController.prototype._findInputs = function () {
    /* Attach to any search input in the header that is a sibling or ancestor wrapper */
    var headerEl = this.container.closest('.site-header') || this.container.closest('header') || document;
    var inputs = headerEl.querySelectorAll('.header__search-input');
    for (var i = 0; i < inputs.length; i++) {
      this.inputs.push(inputs[i]);
    }
  };

  PredictiveSearchController.prototype._bindEvents = function () {
    var self = this;
    var debouncedSearch = debounce(function (query) { self._fetchResults(query); }, DEBOUNCE_MS);

    this.inputs.forEach(function (input) {
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-controls', 'predictive-search-results');

      input.addEventListener('input', function () {
        var q = input.value.trim();
        if (q.length < MIN_QUERY_LENGTH) {
          self._close();
          return;
        }
        self._showLoading();
        debouncedSearch(q);
      });

      input.addEventListener('focus', function () {
        var q = input.value.trim();
        if (q.length >= MIN_QUERY_LENGTH && self.activeQuery === q) {
          self._open();
        }
      });

      /* Sync input values across desktop/mobile */
      input.addEventListener('input', function () {
        self.inputs.forEach(function (otherInput) {
          if (otherInput !== input) otherInput.value = input.value;
        });
      });
    });

    /* Keyboard navigation */
    document.addEventListener('keydown', function (e) {
      if (!self.isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          self._navigate(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          self._navigate(-1);
          break;
        case 'Enter':
          if (self.selectedIndex >= 0) {
            e.preventDefault();
            self._selectCurrent();
          }
          break;
        case 'Escape':
          e.preventDefault();
          self._close();
          self.inputs.forEach(function (inp) { inp.blur(); });
          break;
      }
    });

    /* Close on outside click */
    document.addEventListener('click', function (e) {
      if (!self.isOpen) return;
      var isInside = self.container.contains(e.target);
      var isInput = false;
      self.inputs.forEach(function (inp) { if (inp === e.target || inp.contains(e.target)) isInput = true; });
      if (!isInside && !isInput) {
        self._close();
      }
    });

    /* Close links inside results */
    this.container.addEventListener('click', function (e) {
      if (e.target.closest('[data-predictive-search-close]')) {
        self._close();
      }
    });
  };

  PredictiveSearchController.prototype._fetchResults = function (query) {
    var self = this;
    this.activeQuery = query;

    /* Check cache */
    var cached = getCached(query.toLowerCase());
    if (cached) {
      self._render(cached, query);
      return;
    }

    /* Abort previous request */
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    var url = this.suggestUrl +
      '?q=' + encodeURIComponent(query) +
      '&resources[type]=' + RESOURCE_TYPES +
      '&resources[limit]=' + Math.max(LIMITS.products, LIMITS.collections, LIMITS.articles);

    fetch(url, { signal: this.abortController.signal })
      .then(function (res) {
        if (!res.ok) throw new Error('Network response ' + res.status);
        return res.json();
      })
      .then(function (json) {
        var resources = json.resources && json.resources.results ? json.resources.results : {};
        setCache(query.toLowerCase(), resources);
        if (self.activeQuery === query) {
          self._render(resources, query);
           
        }
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        console.error('[PredictiveSearch]', err);
        self._hideLoading();
      });
  };

  PredictiveSearchController.prototype._render = function (resources, query) {
    /* Immediately hide loader before painting results */
    this._hideLoading();

    var totalResults = 0;

    /* Products */
    var products = (resources.products || []).slice(0, LIMITS.products);
    if (products.length > 0) {
      this.groups.products.list.innerHTML = products.map(function (p) { return buildProductItem(p, query); }).join('');
      this.groups.products.wrapper.hidden = false;
      totalResults += products.length;
    } else {
      this.groups.products.wrapper.hidden = true;
      this.groups.products.list.innerHTML = '';
    }

    /* Collections */
    var collections = (resources.collections || []).slice(0, LIMITS.collections);
    if (collections.length > 0) {
      this.groups.collections.list.innerHTML = collections.map(function (c) { return buildCollectionItem(c, query); }).join('');
      this.groups.collections.wrapper.hidden = false;
      totalResults += collections.length;
    } else {
      this.groups.collections.wrapper.hidden = true;
      this.groups.collections.list.innerHTML = '';
    }

    /* Articles */
    var articles = (resources.articles || []).slice(0, LIMITS.articles);
    if (articles.length > 0) {
      this.groups.articles.list.innerHTML = articles.map(function (a) { return buildArticleItem(a, query); }).join('');
      this.groups.articles.wrapper.hidden = false;
      totalResults += articles.length;
    } else {
      this.groups.articles.wrapper.hidden = true;
      this.groups.articles.list.innerHTML = '';
    }

    /* No results */
    if (totalResults === 0) {
      this.noResultsEl.hidden = false;
      this.footerEl.hidden = true;
    } else {
      this.noResultsEl.hidden = true;
      this.footerEl.hidden = false;
      var viewAllHref = this.searchUrl + '?q=' + encodeURIComponent(query);
      this.viewAllBtn.setAttribute('href', viewAllHref);
    }

    /* Screen reader announcement */
    if (this.statusEl) {
      this.statusEl.textContent = totalResults > 0
        ? totalResults + ' results found for "' + query + '"'
        : 'No results found for "' + query + '"';
    }

    this._open();
    this.selectedIndex = -1;
  };

  /* ---- UI state helpers ---- */
  PredictiveSearchController.prototype._open = function () {
    this.resultsWrapper.hidden = false;
    this.container.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  };

  PredictiveSearchController.prototype._close = function () {
    this.resultsWrapper.hidden = true;
    this.container.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
    this.selectedIndex = -1;
    this._hideLoading();
    this._clearHighlightedItem();
  };

  PredictiveSearchController.prototype._showLoading = function () {
    if (!this.isOpen) this._open();
    this.loadingEl.hidden = false;
  };

  PredictiveSearchController.prototype._hideLoading = function () {
    this.loadingEl.hidden = true;
  };

  /* ---- Keyboard navigation ---- */
  PredictiveSearchController.prototype._getNavigableItems = function () {
    return this.resultsWrapper.querySelectorAll('.predictive-search__item:not([hidden])');
  };

  PredictiveSearchController.prototype._navigate = function (direction) {
    var items = this._getNavigableItems();
    if (!items.length) return;

    this._clearHighlightedItem();
    this.selectedIndex += direction;

    if (this.selectedIndex < 0) this.selectedIndex = items.length - 1;
    if (this.selectedIndex >= items.length) this.selectedIndex = 0;

    var item = items[this.selectedIndex];
    item.classList.add('predictive-search__item--active');
    item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    item.focus();
  };

  PredictiveSearchController.prototype._selectCurrent = function () {
    var items = this._getNavigableItems();
    if (this.selectedIndex < 0 || this.selectedIndex >= items.length) return;
    var link = items[this.selectedIndex].querySelector('a');
    if (link) link.click();
  };

  PredictiveSearchController.prototype._clearHighlightedItem = function () {
    var active = this.resultsWrapper.querySelectorAll('.predictive-search__item--active');
    for (var i = 0; i < active.length; i++) {
      active[i].classList.remove('predictive-search__item--active');
    }
  };

  /* ---- Boot ---- */
  function init() {
    var containers = document.querySelectorAll('[data-predictive-search]');
    containers.forEach(function (el) {
      new PredictiveSearchController(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
